import React, { useState, useEffect, useMemo } from "react";
import {
  X, Plus, Trash2, Save, ArrowLeft, Code, Users, Clock, HardDrive
} from "lucide-react";
import Editor from "react-simple-code-editor";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-java";
import "prismjs/themes/prism-tomorrow.css";
import "./CodingPractice.css";

const studentTypeOptions = [
  { value: "zorvixe_core", label: "Zorvixe Core" },
  { value: "zorvixe_pro", label: "Zorvixe Pro" },
  { value: "zorvixe_elite", label: "Zorvixe Elite" },
];

const difficultyOptions = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

const JavaCodingPractice = ({
  subtopicId: propSubtopicId,
  practiceId,
  onCancel,
  onSuccess,
  editData = null,
}) => {
  const [practice, setPractice] = useState({
    title: "",
    description: "",
    allowed_student_types: ["zorvixe_core", "zorvixe_pro", "zorvixe_elite"],
  });
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isEditing = !!practiceId || !!editData;

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
          [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
          [{ color: [] }, { background: [] }],
          ["link", "image"],
          ["clean"],
        ],
        handlers: {
          image: function () {
            const input = document.createElement("input");
            input.setAttribute("type", "file");
            input.setAttribute("accept", "image/*");
            input.click();

            input.onchange = async () => {
              const file = input.files[0];
              const formData = new FormData();
              formData.append("image", file);

              try {
                const res = await fetch(
                  "https://apione.zorvixetechnologies.in/admin/java/upload-image",
                  { method: "POST", body: formData }
                );
                const data = await res.json();
                if (data.success) {
                  const quill = this.quill;
                  const range = quill.getSelection();
                  const position = range ? range.index : 0;
                  quill.insertEmbed(position, "image", data.url);
                } else {
                  alert("Image upload failed");
                }
              } catch (error) {
                console.error("Upload error:", error);
                alert("Image upload failed");
              }
            };
          },
        },
      },
      clipboard: { matchVisual: false },
    }),
    []
  );

  useEffect(() => {
    if (practiceId) {
      fetchPractice();
    } else if (editData) {
      setPractice({
        title: editData.title || "",
        description: editData.description || "",
        allowed_student_types: editData.allowed_student_types || [
          "zorvixe_core", "zorvixe_pro", "zorvixe_elite"
        ],
      });
      const effectiveSid = editData.subtopic_id || propSubtopicId;
      fetchProblems(editData.id, effectiveSid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, editData, propSubtopicId]);

  const fetchPractice = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `https://apione.zorvixetechnologies.in/admin/java/coding-practices/${practiceId}`
      );
      const data = await res.json();
      if (data.success) {
        setPractice(data.data);
        await fetchProblems(practiceId, data.data.subtopic_id);
      } else {
        alert("Failed to load practice");
      }
    } catch (error) {
      console.error("Error fetching practice:", error);
      alert("Error loading practice");
    } finally {
      setLoading(false);
    }
  };

  const fetchProblems = async (pid, sid) => {
    const effectiveSid = sid || propSubtopicId;
    if (!effectiveSid) {
      console.error("No subtopic ID available to fetch problems");
      return;
    }
    try {
      const res = await fetch(
        `https://apione.zorvixetechnologies.in/admin/java/subtopics/${effectiveSid}/content`
      );
      const data = await res.json();
      if (data.success) {
        const codingProblems = data.data.filter(
          (c) => c.content_type === "coding" && String(c.practice_id) === String(pid)
        );
        
        const problemsWithAccess = codingProblems.map((p) => ({
          ...p,
          allowed_student_types: p.allowed_student_types || [
            "zorvixe_core", "zorvixe_pro", "zorvixe_elite"
          ],
          difficulty: p.difficulty || "easy",
          score: p.score || 0,
          test_cases: p.test_cases && p.test_cases.length > 0 
            ? p.test_cases 
            : [{ input: "", expected_output: "", is_sample: true }],
        }));
        setProblems(problemsWithAccess);
      }
    } catch (error) {
      console.error("Error fetching problems:", error);
    }
  };

  const addProblem = () => {
    setProblems((prev) => [
      ...prev,
      {
        id: `temp-${Date.now()}`,
        coding_title: "",
        coding_description: "",
        starter_code: "",
        difficulty: "easy",
        score: 0,
        test_cases: [{ input: "", expected_output: "", is_sample: true }],
        coding_time_limit: 2,
        coding_memory_limit: 256,
        allowed_student_types: [...practice.allowed_student_types],
      },
    ]);
  };

  const updateProblem = (index, field, value) => {
    setProblems((prev) => 
      prev.map((prob, i) => (i === index ? { ...prob, [field]: value } : prob))
    );
  };

  const updateProblemAccess = (index, type) => {
    setProblems((prev) =>
      prev.map((prob, i) => {
        if (i !== index) return prob;
        const current = prob.allowed_student_types || [];
        return {
          ...prob,
          allowed_student_types: current.includes(type)
            ? current.filter((t) => t !== type)
            : [...current, type],
        };
      })
    );
  };

  const addTestCase = (problemIndex, isSample = true) => {
    setProblems((prev) =>
      prev.map((prob, i) => {
        if (i !== problemIndex) return prob;
        return {
          ...prob,
          test_cases: [
            ...(prob.test_cases || []),
            { input: "", expected_output: "", is_sample: isSample },
          ],
        };
      })
    );
  };

  const updateTestCase = (problemIndex, testIndex, field, value) => {
    setProblems((prev) =>
      prev.map((prob, i) => {
        if (i !== problemIndex) return prob;
        return {
          ...prob,
          test_cases: prob.test_cases.map((tc, j) =>
            j === testIndex ? { ...tc, [field]: value } : tc
          ),
        };
      })
    );
  };

  const removeTestCase = (problemIndex, testIndex) => {
    setProblems((prev) =>
      prev.map((prob, i) => {
        if (i !== problemIndex) return prob;
        return {
          ...prob,
          test_cases: prob.test_cases.filter((_, j) => j !== testIndex),
        };
      })
    );
  };

  const removeProblem = async (index) => {
    const problem = problems[index];
    if (problem.id && problem.id.toString().startsWith("temp-")) {
      setProblems((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    if (!window.confirm("Delete this problem? This action cannot be undone.")) return;

    try {
      const res = await fetch(`https://apione.zorvixetechnologies.in/admin/java/content/${problem.id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        setProblems((prev) => prev.filter((_, i) => i !== index));
      } else {
        alert("Failed to delete problem");
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert("Error deleting problem");
    }
  };

  const handlePracticeTypeToggle = (type) => {
    setPractice((prev) => ({
      ...prev,
      allowed_student_types: prev.allowed_student_types.includes(type)
        ? prev.allowed_student_types.filter((t) => t !== type)
        : [...prev.allowed_student_types, type],
    }));
  };

  const handleDeletePractice = async () => {
    if (!isEditing) return;
    if (!window.confirm("Delete this entire practice? All problems will be detached.")) return;
    
    setDeleting(true);
    try {
      const effectivePracticeId = practiceId || editData?.id;
      const res = await fetch(`https://apione.zorvixetechnologies.in/admin/java/coding-practices/${effectivePracticeId}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) onSuccess();
      else alert("Failed to delete practice");
    } catch (error) {
      console.error("Delete practice error:", error);
      alert("Error deleting practice");
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async () => {
    if (!practice.title.trim()) { alert("Practice title is required"); return; }
    if (practice.allowed_student_types.length === 0) { alert("Please select at least one student type for the practice"); return; }
    if (problems.length === 0) { alert("Please add at least one coding problem"); return; }

    for (let i = 0; i < problems.length; i++) {
      const p = problems[i];
      if (!p.coding_title.trim()) { alert(`Problem ${i + 1} title is required`); return; }
      if (!p.coding_description || p.coding_description === "<p><br></p>") { alert(`Problem ${i + 1} description is required`); return; }
      if (!p.allowed_student_types || p.allowed_student_types.length === 0) { alert(`Please select at least one student type for Problem ${i + 1}`); return; }
      if (!p.test_cases || p.test_cases.length === 0) { alert(`Problem ${i + 1} must have at least one test case`); return; }
      for (let j = 0; j < p.test_cases.length; j++) {
        const tc = p.test_cases[j];
        if (!tc.input.trim() || !tc.expected_output.trim()) {
          alert(`Test case ${j + 1} in Problem ${i + 1} must have both input and expected output`);
          return;
        }
      }
    }

    setSaving(true);
    try {
      let practiceResult;
      const effectivePracticeId = practiceId || editData?.id;
      const effectiveSid = editData?.subtopic_id || propSubtopicId;

      // 1. Save or Update the Practice details
      if (isEditing) {
        const res = await fetch(`https://apione.zorvixetechnologies.in/admin/java/coding-practices/${effectivePracticeId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(practice),
        });
        practiceResult = await res.json();
      } else {
        const res = await fetch(`https://apione.zorvixetechnologies.in/admin/java/subtopics/${effectiveSid}/coding-practices`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(practice),
        });
        practiceResult = await res.json();
      }

      if (!practiceResult.success) throw new Error(practiceResult.error || "Failed to save practice");
      const finalPracticeId = practiceResult.data.id;

      // 2. Map problems to fetch promises so they save reliably
      const problemPromises = problems.map(async (problem) => {
        if (problem.id && !problem.id.toString().startsWith("temp-")) {
          // UPDATE EXISTING PROBLEM
          const res = await fetch(`https://apione.zorvixetechnologies.in/admin/java/content/${problem.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              content_type: "coding",
              coding_title: problem.coding_title,
              coding_description: problem.coding_description,
              starter_code: problem.starter_code,
              testCases: problem.test_cases,
              coding_time_limit: problem.coding_time_limit,
              coding_memory_limit: problem.coding_memory_limit,
              allowed_student_types: problem.allowed_student_types,
              difficulty: problem.difficulty,
              score: problem.score,
              practice_id: finalPracticeId // ensure it remains linked
            }),
          });
          const result = await res.json();
          if(!result.success) throw new Error(`Problem Update failed: ${result.message || result.error}`);
        } else {
          // CREATE NEW PROBLEM
          const res = await fetch(`https://apione.zorvixetechnologies.in/admin/java/subtopics/${effectiveSid}/coding`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              title: problem.coding_title,
              description: problem.coding_description,
              starterCode: problem.starter_code,
              testCases: problem.test_cases,
              time_limit: problem.coding_time_limit,
              memory_limit: problem.coding_memory_limit,
              allowed_student_types: problem.allowed_student_types,
              difficulty: problem.difficulty,
              score: problem.score,
              practice_id: finalPracticeId, 
            }),
          });
          const result = await res.json();
          if(!result.success) throw new Error(`Problem Create failed: ${result.message || result.error}`);
        }
      });

      // Execute all problem updates simultaneously 
      await Promise.all(problemPromises);

      onSuccess();
    } catch (error) {
      console.error("Save error:", error);
      alert(error.message || "Failed to save coding practice and problems");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="coddd-coding-practice-container">
      <div className="coddd-coding-practice-header">
        <button onClick={onCancel} className="coddd-coding-practice-back-btn">
          <ArrowLeft size={16} />
          Back
        </button>
        <div style={{ display: "flex", gap: "8px" }}>
          {isEditing && (
            <button
              onClick={handleDeletePractice}
              className="coddd-coding-practice-delete-btn"
              disabled={deleting}
              style={{ backgroundColor: "#dc2626", color: "white", padding: "8px 14px", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}
            >
              <Trash2 size={16} />
              {deleting ? "Deleting..." : "Delete Practice"}
            </button>
          )}
          <button
            onClick={handleSubmit}
            className="coddd-coding-practice-save-btn"
            disabled={saving}
          >
            <Save size={16} />
            {saving ? "Saving..." : "Save Practice"}
          </button>
        </div>
      </div>

      <div className="coddd-coding-practice-form">
        <div className="coddd-coding-practice-field coddd-title-field">
          <input
            type="text"
            value={practice.title}
            onChange={(e) => setPractice({ ...practice, title: e.target.value })}
            placeholder="Untitled Practice..."
            className="coddd-notion-title-input"
          />
        </div>

        <div className="coddd-coding-practice-field">
          <ReactQuill
            theme="bubble"
            value={practice.description || ""}
            onChange={(value) => setPractice({ ...practice, description: value })}
            modules={quillModules}
            placeholder="Add a description... (Highlight text for options)"
            className="coddd-quill-editor coddd-notion-body-input"
          />
        </div>

        <div className="coddd-coding-practice-field coddd-access-block">
          <label className="coddd-notion-label">Practice Access</label>
          <div className="coddd-student-type-checkboxes">
            {studentTypeOptions.map((option) => (
              <label key={option.value} className="coddd-student-type-checkbox" style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={practice.allowed_student_types.includes(option.value)}
                  onChange={() => handlePracticeTypeToggle(option.value)}
                />
                <Users size={14} style={{ marginLeft: "6px", marginRight: "4px" }} />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="coddd-divider"></div>

        <div className="coddd-coding-problems-section">
          <div className="coddd-coding-problems-header">
            <h3 className="coddd-notion-heading">Problems</h3>
          </div>

          {problems.map((problem, pIndex) => (
            <div key={problem.id} className="coddd-coding-problem-card">
              <div className="coddd-coding-problem-header">
                <div className="coddd-problem-badge">Problem {pIndex + 1}</div>
                <button
                  onClick={() => removeProblem(pIndex)}
                  className="coddd-coding-remove-problem-btn"
                  title="Delete Problem"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              <div className="coddd-coding-problem-field">
                <input
                  type="text"
                  value={problem.coding_title}
                  onChange={(e) => updateProblem(pIndex, "coding_title", e.target.value)}
                  placeholder="Problem Title"
                  className="coddd-notion-subtitle-input"
                />
              </div>

              <div className="coddd-coding-problem-field">
                <ReactQuill
                  theme="bubble"
                  value={problem.coding_description || ""}
                  onChange={(value) => updateProblem(pIndex, "coding_description", value)}
                  modules={quillModules}
                  placeholder="Type problem description here..."
                  className="coddd-quill-editor coddd-notion-body-input"
                />
              </div>

              <div className="coddd-coding-difficulty-row">
                <div className="coddd-coding-difficulty-field">
                  <label className="coddd-notion-label">Difficulty</label>
                  <select
                    className="coddd-notion-select"
                    value={problem.difficulty || "easy"}
                    onChange={(e) => updateProblem(pIndex, "difficulty", e.target.value)}
                  >
                    {difficultyOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="coddd-coding-score-field">
                  <label className="coddd-notion-label">Score (points)</label>
                  <input
                    type="number"
                    value={problem.score || 0}
                    onChange={(e) => updateProblem(pIndex, "score", parseInt(e.target.value) || 0)}
                    min="0"
                    className="coddd-notion-number-input"
                  />
                </div>
              </div>

              <div className="coddd-coding-problem-field-code">
                <label className="coddd-notion-label">
                  <Code size={14} style={{ marginRight: "4px" }} />
                  Starter Code
                </label>
                <div className="coddd-notion-code-block">
                  <Editor
                    value={problem.starter_code || ""}
                    onValueChange={(code) => updateProblem(pIndex, "starter_code", code)}
                    highlight={(code) => highlight(code, languages.java)}
                    padding={15}
                    textareaClassName="coddd-editor-textarea"
                    className="coddd-editor"
                  />
                </div>
              </div>

              <div className="coddd-coding-problem-field coddd-access-block">
                <label className="coddd-notion-label">Student Access</label>
                <div className="coddd-student-type-checkboxes">
                  {studentTypeOptions.map((option) => (
                    <label key={option.value} className="coddd-student-type-checkbox" style={{ display: "flex", alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={(problem.allowed_student_types || practice.allowed_student_types).includes(option.value)}
                        onChange={() => updateProblemAccess(pIndex, option.value)}
                      />
                      <Users size={14} style={{ marginLeft: "6px", marginRight: "4px" }} />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="coddd-coding-limits-row">
                <div className="coddd-coding-limit-field">
                  <label className="coddd-notion-label">
                    <Clock size={14} /> Time Limit (s)
                  </label>
                  <input
                    type="number"
                    value={problem.coding_time_limit}
                    onChange={(e) => updateProblem(pIndex, "coding_time_limit", parseInt(e.target.value) || 2)}
                    min="1"
                    className="coddd-notion-number-input"
                  />
                </div>
                <div className="coddd-coding-limit-field">
                  <label className="coddd-notion-label">
                    <HardDrive size={14} /> Memory Limit (MB)
                  </label>
                  <input
                    type="number"
                    value={problem.coding_memory_limit}
                    onChange={(e) => updateProblem(pIndex, "coding_memory_limit", parseInt(e.target.value) || 256)}
                    min="16"
                    className="coddd-notion-number-input"
                  />
                </div>
              </div>

              <div className="coddd-coding-testcases-section">
                <div className="coddd-coding-testcases-header">
                  <label className="coddd-notion-label">Test Cases</label>
                  <div className="coddd-testcase-actions">
                    <button onClick={() => addTestCase(pIndex, true)} className="coddd-notion-text-btn">
                      <Plus size={14} /> Sample
                    </button>
                    <button onClick={() => addTestCase(pIndex, false)} className="coddd-notion-text-btn">
                      <Plus size={14} /> Hidden
                    </button>
                  </div>
                </div>

                {problem.test_cases?.map((tc, tIndex) => (
                  <div key={tIndex} className="coddd-coding-testcase-row">
                    <span className={`coddd-coding-testcase-badge ${tc.is_sample ? "sample" : "hidden"}`}>
                      {tc.is_sample ? "Sample" : "Hidden"}
                    </span>
                    <input
                      type="text"
                      value={tc.input}
                      onChange={(e) => updateTestCase(pIndex, tIndex, "input", e.target.value)}
                      placeholder="Input"
                      className="coddd-coding-testcase-input"
                    />
                    <div className="coddd-testcase-arrow">→</div>
                    <input
                      type="text"
                      value={tc.expected_output}
                      onChange={(e) => updateTestCase(pIndex, tIndex, "expected_output", e.target.value)}
                      placeholder="Expected Output"
                      className="coddd-coding-testcase-output"
                    />
                    <button onClick={() => removeTestCase(pIndex, tIndex)} className="coddd-coding-remove-testcase-btn">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <button onClick={addProblem} className="coddd-coding-add-problem-btn">
            <Plus size={16} />
            <span>Add another problem</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default JavaCodingPractice;